import { projectService } from '@common/api/projectService';
import { createProjectSlug } from './projectSlug';
import {
  type Brand,
  type BrandFeatureAvailability,
  type BrandMember,
  type BrandIntegrationAssignment,
  type ProjectOverviewMembership,
  type BrandResourceAssignment,
  type IntegrationConnection,
  type IntegrationResource,
} from '@common/api/brandGroupService';
import { useAuth } from '@/contexts/AuthContext';
import type { WorkspaceIntegration } from '@/contexts/AuthContext/useWorkspaceIntegrations';
import type { WorkspaceMemberDisplay } from '@/contexts/AuthContext/useWorkspaceMembers';
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  BrandConnection,
  BrandConnectionStatus,
  BrandGroupData,
  BrandRecord,
  emptyBrandGroupData,
  BrandResource,
  BrandUser,
} from '@/components/Settings/components/brandGroupViewModel';
import { migrateLegacyConnectionLabel } from '@/components/Settings/components/businessGroupLabelMigration';
import {
  applyDefaultBusinessGroupMarker,
  getDefaultBusinessGroupId,
  normalizeDefaultBusinessGroup,
} from './defaultBusinessGroup';
import { hydrateShopifyConnectionDomains } from './shopifyBrandConnectionHydration';
import { getAdminProductFeatures } from '@/utils/adminProductFeatures';

type CreateBrandInput = Pick<BrandRecord, 'name' | 'domain' | 'description' | 'market' | 'logoUrl'> & { scope?: 'workspace' | 'private' };
type UpdateBrandInput = Pick<BrandRecord, 'name' | 'domain' | 'description' | 'logoUrl' | 'market'>;
export type ProjectMutationOptions = { refresh?: boolean; maxAccessLevel?: 'DISABLED' | 'READ' | 'WRITE' };
export type ProjectResourceAssignmentInput = {
  resource: BrandResource;
  maxAccessLevel?: 'DISABLED' | 'READ' | 'WRITE';
};

type BrandGroupContextValue = {
  data: BrandGroupData;
  feature: BrandFeatureAvailability | null;
  isLoading: boolean;
  error: string | null;
  createBrand: (input: CreateBrandInput) => Promise<string>;
  updateBrand: (brandId: string, input: UpdateBrandInput) => Promise<void>;
  deleteBrand: (brandId: string) => Promise<void>;
  assignResourceToBrand: (brandId: string, resource: BrandResource, options?: ProjectMutationOptions) => Promise<void>;
  assignResourcesToBrand: (brandId: string, resources: BrandResource[], options?: ProjectMutationOptions) => Promise<void>;
  syncResourceAssignmentsToBrand: (brandId: string, assignments: ProjectResourceAssignmentInput[], removals: BrandResource[], options?: Pick<ProjectMutationOptions, 'refresh'>) => Promise<void>;
  removeResourceFromBrand: (brandId: string, resource: BrandResource, options?: ProjectMutationOptions) => Promise<void>;
  assignIntegrationToBrand: (brandId: string, connectionId: number, maxAccessLevel?: 'DISABLED' | 'READ' | 'WRITE', options?: ProjectMutationOptions) => Promise<void>;
  assignIntegrationsToBrand: (brandId: string, connectionIds: number[], maxAccessLevel?: 'DISABLED' | 'READ' | 'WRITE', options?: ProjectMutationOptions) => Promise<void>;
  removeIntegrationFromBrand: (brandId: string, connectionId: number, options?: ProjectMutationOptions) => Promise<void>;
  updateResourceLabel: (brandId: string, resource: BrandResource, label: string, options?: ProjectMutationOptions) => Promise<void>;
  upsertBrandMember: (brandId: string, userId: string, role: 'VIEW' | 'EDIT') => Promise<void>;
  removeBrandMember: (brandId: string, userId: string) => Promise<void>;
  refresh: () => Promise<void>;
};

const BrandGroupContext = createContext<BrandGroupContextValue | null>(null);

const resourceIdentity = (resource: Pick<IntegrationResource, 'connectionId' | 'provider' | 'resourceType' | 'resourceKey'>): string =>
  `${resource.connectionId ?? 'none'}:${resource.provider}:${resource.resourceType}:${resource.resourceKey}`;

const toBrandRecord = (brand: Brand): BrandRecord => ({
  id: brand.id,
  name: brand.name,
  domain: brand.domain || '',
  market: '',
  description: brand.description || '',
  logoUrl: brand.logoUrl || null,
  accent: '#302B59',
  createdByUserId: brand.createdBy || undefined,
  primaryOwnerUserId: brand.ownerId || brand.createdBy || undefined,
  memberRole: brand.memberRole || null,
  isDefault: brand.isDefault ?? brand.default,
  scope: brand.visibility === 'PRIVATE' ? 'private' : 'workspace',
  visibility: brand.visibility || 'WORKSPACE',
});

type WorkspaceMemberUserLike = {
  id: string | number;
  userId?: string | null;
  displayName?: string;
  email?: string;
  role?: string;
};

type MembershipRow = {
  brandId: string;
  member: BrandMember;
};

const toMembershipRows = (memberships: ProjectOverviewMembership[]): MembershipRow[] =>
  memberships.map((membership) => ({
    brandId: membership.brandId,
    member: {
      userId: membership.userId,
      role: membership.role,
      status: membership.status,
      email: membership.email || undefined,
      name: membership.name || undefined,
    },
  }));

const groupResourceAssignmentsByBrand = (assignments: BrandResourceAssignment[]) =>
  assignments.reduce((map, assignment) => {
    const brandId = assignment.brandId;
    map.set(brandId, [...(map.get(brandId) || []), assignment]);
    return map;
  }, new Map<string, BrandResourceAssignment[]>());

const groupIntegrationAssignmentsByBrand = (assignments: BrandIntegrationAssignment[]) =>
  assignments.reduce((map, assignment) => {
    const brandId = assignment.projectId;
    map.set(brandId, [...(map.get(brandId) || []), assignment]);
    return map;
  }, new Map<string, BrandIntegrationAssignment[]>());

type BrandGroupDetailState = {
  workspaceId: string;
  assignmentDataBrands: Brand[];
  resources: IntegrationResource[];
  contractIntegrations: IntegrationConnection[];
  integrationAssignmentsByBrand: Map<string, BrandIntegrationAssignment[]>;
  resourceAssignmentsByBrand: Map<string, BrandResourceAssignment[]>;
  membershipRows: MembershipRow[];
};

const toUser = (member: { id: string; displayName?: string; email?: string; role?: string }): BrandUser => {
  const role = String(member.role || '').toUpperCase();
  return {
    id: member.id,
    name: member.displayName || member.email || member.id,
    email: member.email || '',
    role: role === 'OWNER' ? 'Owner' : role === 'ADMIN' ? 'Admin' : 'Member',
  };
};

const buildWorkspaceUsers = (
  workspaceMembers: WorkspaceMemberUserLike[],
  membershipRows: MembershipRow[],
): BrandUser[] => {
  const users = new Map<string, BrandUser>();
  for (const member of workspaceMembers) {
    // Brand membership APIs use the auth user id, not the workspace-membership row id.
    const memberId = String(member.userId || member.id);
    users.set(memberId, toUser({ ...member, id: memberId }));
  }
  for (const row of membershipRows) {
    if (!users.has(row.member.userId)) {
      users.set(
        String(row.member.userId),
        toUser({
          id: String(row.member.userId),
          displayName: row.member.name || undefined,
          email: row.member.email || undefined,
        }),
      );
    }
  }
  return [...users.values()];
};

const connectionStatus = (integration: IntegrationConnection): BrandConnectionStatus =>
  String(integration.status).toUpperCase() === 'ACTIVE'
    ? 'connected'
    : 'sync-issue';

const buildConnections = (
  integrations: IntegrationConnection[],
  resources: IntegrationResource[],
  brands: Brand[],
  integrationAssignmentsByBrand: Map<string, BrandIntegrationAssignment[]>,
  resourceAssignmentsByBrand: Map<string, BrandResourceAssignment[]>,
): BrandConnection[] => {
  const integrationBrandIds = new Map<string, string[]>();
  const integrationBrandAccessCeilings = new Map<string, Record<string, 'DISABLED' | 'READ' | 'WRITE'>>();
  const resourceBrandIds = new Map<string, string[]>();
  const resourceBrandAccessCeilings = new Map<string, Record<string, 'DISABLED' | 'READ' | 'WRITE'>>();
  const assignedBrandLabels = new Map<string, Record<string, string>>();
  for (const brand of brands) {
    for (const assignment of integrationAssignmentsByBrand.get(brand.id) || []) {
      const key = String(assignment.connectionId);
      integrationBrandIds.set(key, [...(integrationBrandIds.get(key) || []), brand.id]);
      integrationBrandAccessCeilings.set(key, {
        ...(integrationBrandAccessCeilings.get(key) || {}),
        [brand.id]: assignment.maxAccessLevel || 'WRITE',
      });
    }
    for (const assignment of resourceAssignmentsByBrand.get(brand.id) || []) {
      const key = resourceIdentity(assignment);
      resourceBrandIds.set(key, [...(resourceBrandIds.get(key) || []), brand.id]);
      resourceBrandAccessCeilings.set(key, {
        ...(resourceBrandAccessCeilings.get(key) || {}),
        [brand.id]: assignment.maxAccessLevel || 'READ',
      });
      if (typeof assignment.label === 'string' && assignment.label.trim()) {
        assignedBrandLabels.set(key, {
          ...(assignedBrandLabels.get(key) || {}),
          [brand.id]: assignment.label.trim(),
        });
      }
    }
  }
  const resourcesByConnection = new Map<string, IntegrationResource[]>();
  for (const resource of resources) {
    if (resource.connectionId == null) continue;
    const key = String(resource.connectionId);
    resourcesByConnection.set(key, [...(resourcesByConnection.get(key) || []), resource]);
  }
  return integrations.map((integration) => {
    const id = String(integration.connectionId);
    const isShopifyIntegration = String(integration.provider).toLowerCase() === 'shopify';
    const connectionResources = resourcesByConnection.get(id) || [];
    const resourceProjectIds = connectionResources.flatMap(
      (resource) => resourceBrandIds.get(resourceIdentity(resource)) || [],
    );
    const brandIds = [...new Set([...(integrationBrandIds.get(id) || []), ...resourceProjectIds])];
    const resourcesWithBrands: BrandResource[] = connectionResources.map((resource) => {
      const assignedResourceBrandIds = [...new Set(resourceBrandIds.get(resourceIdentity(resource)) || [])];
      return {
        ...resource,
        brandIds: assignedResourceBrandIds,
        brandAccessCeilings: resourceBrandAccessCeilings.get(resourceIdentity(resource)) || {},
        brandLabels: migrateLegacyConnectionLabel({
          legacyLabel: resource.legacyConnectionLabel,
          brandIds: assignedResourceBrandIds,
          existingLabels: assignedBrandLabels.get(resourceIdentity(resource)) || {},
        }),
      };
    });
    return {
      id,
      provider: integration.provider,
      // Shopify's readable store domain is the external account name. Keep it
      // ahead of the generic connector label so Project assignment rows show
      // the same value as the Integrations page.
      accountName: isShopifyIntegration
        ? integration.externalAccountName || integration.label || integration.provider
        : integration.label || integration.externalAccountName || integration.provider,
      accountDetail: integration.externalAccountId || integration.slugId || '',
      brandId: brandIds[0] || null,
      brandIds,
      brandAccessCeilings: integrationBrandAccessCeilings.get(id) || {},
      status: connectionStatus(integration),
      connectedByUserId: integration.connectedBy || '',
      connectedByName: integration.connectedByName || undefined,
      connectedAt: integration.connectedAt || '',
      assignedByUserId: null,
      assignedAt: null,
      canUseConnection: integration.canUseIntegration,
      canManageConnection: integration.canManageConnection,
      canManageAccess: integration.canManageAccess,
      resources: resourcesWithBrands,
    };
  });
};

export const BrandGroupProvider = ({ children }: { children: ReactNode }) => {
  const { currentWorkspace, integrations, workspaceMembers } = useAuth();
  const workspaceId = currentWorkspace?.id;
  const brandManagementEnabled = getAdminProductFeatures().brandManagement;
  const [data, setData] = useState<BrandGroupData>(emptyBrandGroupData);
  const [dataWorkspaceId, setDataWorkspaceId] = useState<string | undefined>(undefined);
  const [feature, setFeature] = useState<BrandFeatureAvailability | null>(null);
  const [featureWorkspaceId, setFeatureWorkspaceId] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const refreshRequestRef = useRef(0);
  const renderedWorkspaceIdRef = useRef(workspaceId);
  const integrationsRef = useRef(integrations.allIntegrations);
  const workspaceMembersRef = useRef(workspaceMembers.members);
  const latestDataStateRef = useRef({ data, dataWorkspaceId });
  const detailStateRef = useRef<BrandGroupDetailState | null>(null);

  useEffect(() => {
    integrationsRef.current = integrations.allIntegrations;
  }, [integrations.allIntegrations]);

  useEffect(() => {
    workspaceMembersRef.current = workspaceMembers.members;
  }, [workspaceMembers.members]);

  useEffect(() => {
    latestDataStateRef.current = { data, dataWorkspaceId };
  }, [data, dataWorkspaceId]);

  // Invalidate an in-flight response synchronously when the selected
  // Workspace changes, before the previous request has a chance to commit.
  if (renderedWorkspaceIdRef.current !== workspaceId) {
    renderedWorkspaceIdRef.current = workspaceId;
    refreshRequestRef.current += 1;
    detailStateRef.current = null;
  }

  const buildProjectedConnections = (
    detailState: BrandGroupDetailState,
    currentIntegrations: WorkspaceIntegration[],
  ): BrandConnection[] =>
    buildConnections(
      hydrateShopifyConnectionDomains(
        detailState.contractIntegrations,
        currentIntegrations,
      ).filter((integration) => String(integration.status).toUpperCase() === 'ACTIVE'),
      detailState.resources,
      detailState.assignmentDataBrands,
      detailState.integrationAssignmentsByBrand,
      detailState.resourceAssignmentsByBrand,
    );

  const refresh = useCallback(async () => {
    const requestSequence = ++refreshRequestRef.current;
    const requestWorkspaceId = workspaceId;
    const isCurrentRequest = () =>
      refreshRequestRef.current === requestSequence &&
      renderedWorkspaceIdRef.current === requestWorkspaceId;

    if (!brandManagementEnabled) {
      if (!isCurrentRequest()) return;
      detailStateRef.current = null;
      setData(emptyBrandGroupData());
      setDataWorkspaceId(requestWorkspaceId);
      setFeature(null);
      setFeatureWorkspaceId(requestWorkspaceId);
      setIsLoading(false);
      setError(null);
      return;
    }

    if (!requestWorkspaceId) {
      if (!isCurrentRequest()) return;
      detailStateRef.current = null;
      setData(emptyBrandGroupData());
      setDataWorkspaceId(undefined);
      setFeature(null);
      setFeatureWorkspaceId(undefined);
      setIsLoading(false);
      setError(null);
      return;
    }
    const hasVisibleWorkspaceData =
      latestDataStateRef.current.dataWorkspaceId === requestWorkspaceId &&
      latestDataStateRef.current.data.brands.length > 0;
    setIsLoading(!hasVisibleWorkspaceData);
    setError(null);
    let committedBootstrap = false;
    try {
      const overviewPromise = projectService.getProjectOverview(requestWorkspaceId);
      const contractPromise = projectService.getIntegrationContract(requestWorkspaceId)
        .catch((cause) => { throw cause; });
      const [nextFeature, overview] = await Promise.all([
        projectService.getFeature(requestWorkspaceId),
        overviewPromise,
      ]);
      if (!isCurrentRequest()) return;
      const brands = overview.brands;
      const assignmentBrands = overview.assignmentBrands;
      const normalizedBrands = normalizeDefaultBusinessGroup(brands);
      const normalizedAssignmentBrands = applyDefaultBusinessGroupMarker(
        assignmentBrands,
        getDefaultBusinessGroupId(normalizedBrands),
      );
      // The navigation list intentionally hides private Projects that the
      // current user does not own. Integration assignments are different:
      // once a connection/resource has been attached to a Workspace Project,
      // it must remain visible in the integrations surface (usually as
      // DISABLED) even when the current user is not that Project's member.
      // Keep membership loading navigation-scoped, but load assignment facts
      // for the dedicated Workspace-wide assignment inventory as well.
      const navigationBrandIds = new Set(normalizedBrands.map((brand) => brand.id));
      const overviewMembershipRows = toMembershipRows(
        overview.memberships.filter((membership) => navigationBrandIds.has(membership.brandId)),
      );
      const assignmentDataBrands = [
        ...new Map(
          [...normalizedBrands, ...normalizedAssignmentBrands].map((brand) => [brand.id, brand]),
        ).values(),
      ];
      setFeature(nextFeature);
      setFeatureWorkspaceId(requestWorkspaceId);
      const bootstrapUsers = buildWorkspaceUsers(
        workspaceMembersRef.current as WorkspaceMemberDisplay[],
        overviewMembershipRows,
      );
      const bootstrapConnections = hasVisibleWorkspaceData
        ? latestDataStateRef.current.data.connections
        : [];
      const bootstrapMemberships = overviewMembershipRows.map(({ brandId, member }) => ({
        brandId,
        userId: member.userId,
        role: member.role,
        status: member.status,
      }));

      if (!isCurrentRequest()) return;
      // Render the Business-group directory as soon as the bootstrap payload is
      // ready. Per-Project members/resources/integration assignments are
      // hydrated below; keeping them in the initial critical path makes large
      // Workspaces wait on N Projects × 3 requests before showing the list.
      setData({
        ...emptyBrandGroupData(),
        totalBrandCount: overview.totalBrandCount,
        projectUsageByCreator: overview.projectUsageByCreator,
        brands: normalizedBrands.map(toBrandRecord),
        assignmentBrands: normalizedAssignmentBrands.map(toBrandRecord),
        connections: bootstrapConnections,
        users: bootstrapUsers,
        memberships: bootstrapMemberships,
      });
      setDataWorkspaceId(requestWorkspaceId);
      committedBootstrap = true;
      setIsLoading(false);

      const contract = await contractPromise;
      // Every Workspace has a default Project. Project visibility is no
      // longer gated by the Workspace subscription or the legacy feature flag;
      // the Project API is authoritative for the current Project set.
      // Resource Contract is required for the Project integration view and
      // resource assignment. Propagate failures so a missing deployment or
      // broken Account ↔ Integrations contract is actionable instead of
      // appearing as an empty Project.
      const resources = contract.resources;
      // Project assignment is inventory/scope management, not a legacy
      // Workspace data-grant decision. Keep every active resource visible so
      // a connection owner or Workspace owner can add a foreign connection to
      // a public Project; the Project grant matrix then starts it at
      // DISABLED for users without access. Agent execution remains fail-closed
      // through the Project-scoped resource contract and Gateway-frozen run selection.
      const usableResources = resources
        .filter((resource) => String(resource.status).toUpperCase() === 'ACTIVE')
        .map((resource) => ({
          ...resource,
          isInResourceContract: true,
        }));
      const membershipRows = overviewMembershipRows;
      const assignmentsByBrand = groupResourceAssignmentsByBrand(overview.resourceAssignments);
      const integrationAssignmentsByBrand = groupIntegrationAssignmentsByBrand(overview.integrationAssignments);
      // A Resource Contract row with canUseResource=false must not suppress a
      // persisted Brand assignment. Keep the assigned row visible (disabled)
      // so the UI can explain the missing grant instead of rendering an empty
      // Brand. Usable Contract rows still win and carry the authoritative
      // access flags.
      const contractResourceKeys = new Set(
        usableResources.map((resource) => resourceIdentity(resource)),
      );
      const persistedAssignmentResources: IntegrationResource[] = [];
      for (const assignments of assignmentsByBrand.values()) {
        for (const assignment of assignments) {
          const identity = resourceIdentity(assignment);
          if (contractResourceKeys.has(identity)) continue;
          contractResourceKeys.add(identity);
          persistedAssignmentResources.push({
            workspaceId: requestWorkspaceId,
            connectionId: assignment.connectionId,
            provider: assignment.provider,
            resourceType: assignment.resourceType,
            resourceKey: assignment.resourceKey,
            sourceMappingType: assignment.sourceMappingType,
            sourceMappingId: assignment.sourceMappingId,
            name: assignment.resourceKey,
            status: 'ACTIVE',
            // Keep stale persisted assignments visible in the owning Project,
            // but never treat them as selectable Workspace inventory. The
            // Account API will (correctly) reject a resource absent from the
            // current contract, so the picker must not offer it as new input.
            isInResourceContract: false,
            canUseResource: false,
            canManageResourceAccess: false,
          });
        }
      }
      const detailState: BrandGroupDetailState = {
        workspaceId: requestWorkspaceId,
        assignmentDataBrands,
        resources: [...usableResources, ...persistedAssignmentResources],
        contractIntegrations: contract.integrations,
        integrationAssignmentsByBrand,
        resourceAssignmentsByBrand: assignmentsByBrand,
        membershipRows,
      };
      detailStateRef.current = detailState;
      const users = buildWorkspaceUsers(workspaceMembersRef.current as WorkspaceMemberDisplay[], membershipRows);
      if (!isCurrentRequest()) return;
      setData({
        ...emptyBrandGroupData(),
        totalBrandCount: overview.totalBrandCount,
        projectUsageByCreator: overview.projectUsageByCreator,
        brands: normalizedBrands.map(toBrandRecord),
        assignmentBrands: normalizedAssignmentBrands.map(toBrandRecord),
        connections: buildProjectedConnections(detailState, integrationsRef.current),
        users,
        memberships: membershipRows.map(({ brandId, member }) => ({
          brandId,
          userId: member.userId,
          role: member.role,
          status: member.status,
        })),
      });
      setDataWorkspaceId(requestWorkspaceId);
    } catch (cause) {
      // Keep API failures separate from entitlement/activation state. A
      // transient 5xx or network error must not render as “not entitled”.
      if (!isCurrentRequest()) return;
      if (committedBootstrap) {
        setError(cause instanceof Error ? cause.message : 'brand_group_detail_load_failed');
        return;
      }
      setData(emptyBrandGroupData());
      setDataWorkspaceId(requestWorkspaceId);
      setError(cause instanceof Error ? cause.message : 'brand_group_load_failed');
      throw cause;
    } finally {
      if (isCurrentRequest()) setIsLoading(false);
    }
  }, [brandManagementEnabled, workspaceId]);

  useEffect(() => { void refresh().catch(() => undefined); }, [refresh]);

  useEffect(() => {
    if (!workspaceId || latestDataStateRef.current.dataWorkspaceId !== workspaceId) return;
    const membershipRows = detailStateRef.current?.workspaceId === workspaceId
      ? detailStateRef.current.membershipRows
      : [];
    setData((currentData) => ({
      ...currentData,
      users: buildWorkspaceUsers(workspaceMembers.members as WorkspaceMemberDisplay[], membershipRows),
    }));
  }, [workspaceId, workspaceMembers.members]);

  useEffect(() => {
    const detailState = detailStateRef.current;
    if (!workspaceId || !detailState || detailState.workspaceId !== workspaceId) return;
    if (latestDataStateRef.current.dataWorkspaceId !== workspaceId) return;
    setData((currentData) => ({
      ...currentData,
      connections: buildProjectedConnections(detailState, integrations.allIntegrations),
    }));
  }, [integrations.allIntegrations, workspaceId]);

  const visibleData = dataWorkspaceId === workspaceId ? data : emptyBrandGroupData();
  const visibleFeature = featureWorkspaceId === workspaceId ? feature : null;
  const visibleIsLoading = Boolean(workspaceId) && (isLoading || workspaceId !== dataWorkspaceId);

  // Existing workspaces are expected to be ENABLED by the release backfill,
  // but missed/new workspaces are initialized asynchronously. Keep the
  // Brand surface in sync without requiring a full page refresh.
  useEffect(() => {
    if (visibleFeature?.state !== 'ENABLING') return;
    const timer = window.setTimeout(() => {
      void refresh().catch(() => undefined);
    }, 2000);
    return () => window.clearTimeout(timer);
  }, [refresh, visibleFeature?.state]);

  const createBrand = useCallback(async (input: CreateBrandInput) => {
    if (!workspaceId) throw new Error('workspace_required');
    const created = await projectService.createProject(workspaceId, {
      name: input.name,
      slug: createProjectSlug(input.name),
      domain: input.domain,
      description: input.description,
      ...(input.logoUrl ? { logoUrl: input.logoUrl } : {}),
      visibility: input.scope === 'private' ? 'PRIVATE' : 'WORKSPACE',
    });
    await refresh();
    return created.id;
  }, [refresh, workspaceId]);

  const updateBrand = useCallback(async (brandId: string, input: UpdateBrandInput) => {
    if (!workspaceId) throw new Error('workspace_required');
    await projectService.updateProject(workspaceId, brandId, {
      name: input.name,
      domain: input.domain,
      description: input.description,
      logoUrl: input.logoUrl,
    });
    await refresh();
  }, [refresh, workspaceId]);

  const deleteBrand = useCallback(async (brandId: string) => {
    if (!workspaceId) throw new Error('workspace_required');
    await projectService.deleteProject(workspaceId, brandId);
    await refresh();
  }, [refresh, workspaceId]);

  const assignResourceToBrand = useCallback(async (brandId: string, resource: BrandResource, options?: ProjectMutationOptions) => {
    if (!workspaceId) throw new Error('workspace_required');
    await projectService.assignResource(workspaceId, brandId, {
      provider: resource.provider,
      resourceType: resource.resourceType,
      resourceKey: resource.resourceKey,
      connectionId: resource.connectionId,
      maxAccessLevel: options?.maxAccessLevel,
    });
    if (options?.refresh !== false) await refresh();
  }, [refresh, workspaceId]);

  const assignResourcesToBrand = useCallback(async (brandId: string, resources: BrandResource[], options?: ProjectMutationOptions) => {
    if (!workspaceId) throw new Error('workspace_required');
    if (resources.length === 0) return;
    await projectService.assignResources(workspaceId, brandId, resources.map((resource) => ({
      provider: resource.provider,
      resourceType: resource.resourceType,
      resourceKey: resource.resourceKey,
      connectionId: resource.connectionId,
      maxAccessLevel: options?.maxAccessLevel,
    })));
    if (options?.refresh !== false) await refresh();
  }, [refresh, workspaceId]);

  const syncResourceAssignmentsToBrand = useCallback(async (
    brandId: string,
    assignments: ProjectResourceAssignmentInput[],
    removals: BrandResource[],
    options?: Pick<ProjectMutationOptions, 'refresh'>,
  ) => {
    if (!workspaceId) throw new Error('workspace_required');
    if (assignments.length === 0 && removals.length === 0) return;
    await projectService.syncResources(
      workspaceId,
      brandId,
      assignments.map(({ resource, maxAccessLevel }) => ({
        provider: resource.provider,
        resourceType: resource.resourceType,
        resourceKey: resource.resourceKey,
        connectionId: resource.connectionId,
        maxAccessLevel,
      })),
      removals.map((resource) => ({
        provider: resource.provider,
        resourceType: resource.resourceType,
        resourceKey: resource.resourceKey,
        connectionId: resource.connectionId,
      })),
    );
    if (options?.refresh !== false) await refresh();
  }, [refresh, workspaceId]);

  const updateResourceLabel = useCallback(async (brandId: string, resource: BrandResource, label: string, options?: ProjectMutationOptions) => {
    if (!workspaceId) throw new Error('workspace_required');
    await projectService.assignResource(workspaceId, brandId, {
      provider: resource.provider,
      resourceType: resource.resourceType,
      resourceKey: resource.resourceKey,
      connectionId: resource.connectionId,
      label: label.trim() || null,
    });
    if (options?.refresh !== false) await refresh();
  }, [refresh, workspaceId]);

  const removeResourceFromBrand = useCallback(async (brandId: string, resource: BrandResource, options?: ProjectMutationOptions) => {
    if (!workspaceId) throw new Error('workspace_required');
    await projectService.removeResource(workspaceId, brandId, {
      provider: resource.provider,
      resourceType: resource.resourceType,
      resourceKey: resource.resourceKey,
      connectionId: resource.connectionId,
    });
    if (options?.refresh !== false) await refresh();
  }, [refresh, workspaceId]);

  const assignIntegrationToBrand = useCallback(async (brandId: string, connectionId: number, maxAccessLevel?: 'DISABLED' | 'READ' | 'WRITE', options?: ProjectMutationOptions) => {
    if (!workspaceId) throw new Error('workspace_required');
    await projectService.assignIntegration(workspaceId, brandId, connectionId, maxAccessLevel);
    if (options?.refresh !== false) await refresh();
  }, [refresh, workspaceId]);

  const assignIntegrationsToBrand = useCallback(async (brandId: string, connectionIds: number[], maxAccessLevel?: 'DISABLED' | 'READ' | 'WRITE', options?: ProjectMutationOptions) => {
    if (!workspaceId) throw new Error('workspace_required');
    if (connectionIds.length === 0) return;
    await projectService.assignIntegrations(workspaceId, brandId, connectionIds, maxAccessLevel);
    if (options?.refresh !== false) await refresh();
  }, [refresh, workspaceId]);

  const removeIntegrationFromBrand = useCallback(async (brandId: string, connectionId: number, options?: ProjectMutationOptions) => {
    if (!workspaceId) throw new Error('workspace_required');
    await projectService.removeIntegration(workspaceId, brandId, connectionId);
    if (options?.refresh !== false) await refresh();
  }, [refresh, workspaceId]);

  const upsertBrandMember = useCallback(async (brandId: string, userId: string, role: 'VIEW' | 'EDIT') => {
    if (!workspaceId) throw new Error('workspace_required');
    await projectService.upsertMember(workspaceId, brandId, { userId, role });
    await refresh();
  }, [refresh, workspaceId]);

  const removeBrandMember = useCallback(async (brandId: string, userId: string) => {
    if (!workspaceId) throw new Error('workspace_required');
    await projectService.removeMember(workspaceId, brandId, userId);
    await refresh();
  }, [refresh, workspaceId]);

  const value = useMemo(() => ({ data: visibleData, feature: visibleFeature, isLoading: visibleIsLoading, error, createBrand, updateBrand, deleteBrand, assignResourceToBrand, assignResourcesToBrand, syncResourceAssignmentsToBrand, removeResourceFromBrand, assignIntegrationToBrand, assignIntegrationsToBrand, removeIntegrationFromBrand, updateResourceLabel, upsertBrandMember, removeBrandMember, refresh }), [assignIntegrationToBrand, assignIntegrationsToBrand, assignResourceToBrand, assignResourcesToBrand, createBrand, deleteBrand, error, refresh, removeBrandMember, removeIntegrationFromBrand, removeResourceFromBrand, syncResourceAssignmentsToBrand, updateBrand, updateResourceLabel, upsertBrandMember, visibleData, visibleFeature, visibleIsLoading]);
  return <BrandGroupContext.Provider value={value}>{children}</BrandGroupContext.Provider>;
};

export const useBrandGroup = (): BrandGroupContextValue => {
  const context = useContext(BrandGroupContext);
  if (!context) throw new Error('useBrandGroup must be used inside BrandGroupProvider');
  return context;
};
